/**
 * @fileoverview Reusable collapsible accordion section for builder configuration panels.
 * @module ui-admin/components/AccordionSection
 *
 * Responsibilities: - Render a single titled, collapsible section with an icon, chevron, and
 *                     animated open/close body (CSS max-height transition).
 *                   - Persist the open/closed state to localStorage when a `storageKey` is given.
 *                   - Provide keyboard-accessible controls (Enter/Space toggle; ArrowUp/ArrowDown
 *                     move focus between sibling section headers).
 * Dependencies:     - React, lucide-react (ChevronDown).
 * Architecture:     - Presentation-only, host-neutral component shared by tenant-web/system-web.
 *                     The only side effect is localStorage (an ephemeral UI preference) — no Dexie,
 *                     no API calls, no mock data. Used by the document builder left panel (H3).
 * Notes:            - ArrowUp/ArrowDown move focus across all `[data-accordion-header]` elements in
 *                     the document so a group of `AccordionSection`s behaves like one accordion.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';

/**
 * @contract AccordionSectionProps
 * Purpose: Configure a single collapsible builder-panel section.
 * Fields:
 * - title: Section heading text (also the accessible name of the toggle).
 * - icon: Optional lucide icon shown left of the title.
 * - defaultOpen: Initial open state when no persisted value exists (default false).
 * - storageKey: Optional localStorage key; when set, open/closed state round-trips across sessions.
 * - children: Section body content (rendered only while open is true for performance).
 * Rules: State is ephemeral UI preference only; never reads or writes Dexie or the network.
 */
export interface AccordionSectionProps {
  title: string;
  icon?: LucideIcon;
  defaultOpen?: boolean;
  storageKey?: string;
  children: React.ReactNode;
}

/** Read the persisted open state for a storage key, falling back to `fallback`. */
function readPersistedOpen(storageKey: string | undefined, fallback: boolean): boolean {
  if (!storageKey || typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    /* localStorage unavailable (private mode / SSR) — use the fallback. */
  }
  return fallback;
}

/**
 * @component AccordionSection
 * Purpose:     Single collapsible section with icon, chevron, animated body, keyboard support.
 * Props:       AccordionSectionProps.
 * Behavior:    Toggles open/closed; persists to localStorage when `storageKey` is provided;
 *              ArrowUp/ArrowDown move focus between sibling accordion headers.
 * Data Source: none (presentation). localStorage is the only side effect.
 * Constraints: no API/Dexie access; no mock data.
 */
export function AccordionSection({ title, icon: Icon, defaultOpen = false, storageKey, children }: AccordionSectionProps) {
  const [open, setOpen] = useState<boolean>(() => readPersistedOpen(storageKey, defaultOpen));
  const headerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, String(open));
    } catch {
      /* ignore persistence failures */
    }
  }, [open, storageKey]);

  const focusSibling = useCallback((dir: -1 | 1) => {
    if (typeof document === 'undefined') return;
    const headers = Array.from(document.querySelectorAll<HTMLElement>('[data-accordion-header]'));
    const current = headerRef.current;
    if (!current) return;
    const idx = headers.indexOf(current);
    if (idx === -1) return;
    const next = headers[idx + dir];
    if (next) next.focus();
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      setOpen((v) => !v);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusSibling(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusSibling(-1);
    }
  }, [focusSibling]);

  return (
    <div className="border-b border-gray-200" data-testid={`accordion-${title}`}>
      <button
        ref={headerRef}
        type="button"
        data-accordion-header
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleKeyDown}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-orange-400"
      >
        {Icon && <Icon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />}
        <span className="flex-1">{title}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      <div
        className="overflow-hidden transition-all duration-200 ease-in-out"
        style={{ maxHeight: open ? 4000 : 0 }}
        aria-hidden={!open}
      >
        {open && <div className="px-3 pb-3 pt-1">{children}</div>}
      </div>
    </div>
  );
}

export default AccordionSection;
